import CbcMarketingPage from "./components/CbcMarketingPage";
import PrivacyConsentPanel from "./components/PrivacyConsentPanel";
import SanctuaryEntranceFlow from "./components/SanctuaryEntranceFlow";

import React, { useCallback, useEffect, useState } from "react";

import { GentleHandoff } from "./GentleHandoff";

import "./App.css";
import ToolkitView from "./components/ToolkitView";

import { authHeaders, logout, validateToken } from "./api/auth";

import Login from "./components/Login";
import TeacherDashboard from "./components/TeacherDashboard";
import AdminDashboard from "./components/AdminDashboard";
import JournalsView from "./components/JournalsView";
import ReflectionsView from "./components/ReflectionsView";
import HomeView from "./components/HomeView";
import { getHomeRecommendation } from "./lib/homeRecommendation";
import ProjectWorkspace from "./components/ProjectWorkspace";
import SanctuaryEntryScreen from "./components/SanctuaryEntryScreen";
import ThankYouPage from "./components/ThankYouPage";
import ConfirmedPage from "./ConfirmedPage";
import WorkshopPage from "./components/WorkshopPage";
import SecureWorkshopPage from "./components/SecureWorkshopPage";
import PublicWelcomeScreen from "./components/PublicWelcomeScreen";
import OrganisationsEntryScreen from "./components/OrganisationsEntryScreen";
import BookOfferPage from "./components/BookOfferPage";
import BookThankYouPage from "./components/BookThankYouPage";
import MembershipConfirmedPage from "./components/MembershipConfirmedPage";
import FirstLoginOnboarding from "./components/FirstLoginOnboarding";

import {
  getProfile,
  getRecentMemories,
  getHomeSummary,
  createMemory,
  deleteMemory,
  Profile,
  getWeeklyReflection,
  Memory,
  WeeklyReflection,
  getJournalEntries,
  createJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  JournalEntry,
  Plan,
  getThreads,
  createThread,
  updateProfilePreferences,
} from "./api/client";

import ThreadWorkspace from "./components/ThreadWorkspace";

const HANDOFF_KEY = "sanctuary:last_handoff_choice";

type SavedHandoff = { choice: string; at: string };

function loadHandoffChoice(): SavedHandoff | null {
  try {
    const raw = localStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as SavedHandoff;
    if (!parsed?.choice || !parsed?.at) return null;

    return parsed;
  } catch {
    return null;
  }
}

function saveHandoffChoice(choice: string) {
  const payload: SavedHandoff = { choice, at: new Date().toISOString() };
  try {
    localStorage.setItem(HANDOFF_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

function prettyChoice(choice: string) {
  return choice
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

type View =
  | "home"
  | "checkin"
  | "thankyou"
  | "journals"
  | "reflections"
  | "toolkit"
  | "handoff"
  | "entrance"
  | "workspace"
  | "projectWorkspace"
  | "firstSteps"
  | "admin"
  | "supervisor"
  | "teacher";


const LAST_VIEW_KEY = "sanctuary:last_view";
const LAST_HOME_DATE_KEY = "sanctuary:last_home_date";
const LAST_CHECKIN_DATE_KEY = "sanctuary:last_checkin_date";


function App() {

const [workspaceThreadId, setWorkspaceThreadId] = useState<number | null>(null);
const [activeProject, setActiveProject] = useState<Plan | null>(null);
const [onboardingProject, setOnboardingProject] = useState(false);
const [onboardingWellbeingChoice, setOnboardingWellbeingChoice] = useState(false);
const [wellbeingPromptDismissed, setWellbeingPromptDismissed] = useState(false);

const [view, setView] = useState<View>(() => {
try {
const today = new Date().toISOString().slice(0, 10);
const last = localStorage.getItem(LAST_VIEW_KEY) as View | null;

const allowed: View[] = [
  "home",
  "checkin",
  "journals",
  "reflections",
  "toolkit",
  "handoff",
  "entrance",
  "workspace",
  "projectWorkspace",
  "firstSteps",
  "admin",
  "supervisor",
  "teacher",
];

if (last && allowed.includes(last)) {
  localStorage.setItem(LAST_HOME_DATE_KEY, today);
  return last;
}

const lastHomeDate = localStorage.getItem(LAST_HOME_DATE_KEY);

if (lastHomeDate !== today) {
  localStorage.setItem(LAST_HOME_DATE_KEY, today);
  return "home";
}

return "home";
  } catch {
    return "entrance";
  }
});

  const handleLogout = async () => {
  try {
    await logout();
  } catch (e) {
    // ignore backend/logout errors
  }

  localStorage.removeItem("token");
  localStorage.removeItem("sanctuary:last_view");

  setProfile(null);
  setMemories([]);
  setHomeSummary(null);
  setJournalEntries([]);
  setWeeklyReflection(null);
  setToken(null);

  setView("entrance");
};

    const handleStartEditJournal = (entry: JournalEntry) => {
      setJournalText(entry.content);
      setEditingJournalId(entry.id);
    };

    const handleCancelEditJournal = () => {
      setEditingJournalId(null);
    };

function setViewPersist(next: View) {
  setView(next);
  try {
    if (next !== "entrance") {
      localStorage.setItem(LAST_VIEW_KEY, next);
    }
  } catch {
    // ignore
  }
}
async function ensureWorkspace(): Promise<number> {
  if (workspaceThreadId !== null) return workspaceThreadId;

  try {
    const threads = await getThreads();
    let thread = threads[0];

    if (!thread) {
      thread = await createThread({
        title: "Sanctuary Project",
        thread_type: "project",
        description: "Main project workspace",
      });
    }

    setWorkspaceThreadId(thread.id);
    return thread.id;
  } catch (err) {
    console.error(err);
    setError("Could not open your workspace.");
    throw err;
  }
}

async function openWorkspace() {
  await ensureWorkspace();
  setOnboardingProject(false);
  setViewPersist("workspace");
}
  // ...rest of your state
  
  const [profile, setProfile] = useState<Profile | null>(null);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [homeSummary, setHomeSummary] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkInText, setCheckInText] = useState("");
  const [energyLevel, setEnergyLevel] = useState<number | null>(null);
  const checkIns = memories.filter((m) => m.entry_type === "checkin");
  const [justCheckedIn, setJustCheckedIn] = useState(false);
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [journalText, setJournalText] = useState("");
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [editingJournalId, setEditingJournalId] = useState<string | null>(null);
  
  const [errorReflection, setErrorReflection] = useState<string | null>(null);
 
  const [weeklyReflection, setWeeklyReflection] = useState<WeeklyReflection | null>(null);
  const [loadingReflection, setLoadingReflection] = useState(false);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [authReady, setAuthReady] = useState(false);

  const [authTick, setAuthTick] = useState(0);
  const [hasEntered, setHasEntered] = useState(false);

  const [stateCheckInComplete, setStateCheckInComplete] = useState(() => {
  const today = new Date().toISOString().slice(0, 10);
  return localStorage.getItem(LAST_CHECKIN_DATE_KEY) === today;
});

  const [currentEnergy, setCurrentEnergy] = useState<number | null>(null);

  const lastHandoff = loadHandoffChoice();

  const loadWeeklyReflection = useCallback(async () => {
  try {
    setLoadingReflection(true);
    setErrorReflection(null);

    const data = await getWeeklyReflection();
    setWeeklyReflection(data);
  } catch (err) {
    console.error(err);
    setErrorReflection("Could not load weekly reflection.");
    setWeeklyReflection(null);
  } finally {
    setLoadingReflection(false);
  }
}, []);

  const inputStyles: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.2)",
  background: "rgba(0,0,0,0.2)",
  color: "inherit",
  fontSize: "1.1rem",
  lineHeight: 1.6,
  fontFamily: "inherit",
  fontWeight: 400,
};

const writingColumn: React.CSSProperties = {
  width: "100%",
  maxWidth: 1100,
  margin: "0 auto",
};

  useEffect(() => {
  if (!token || !authReady) return;
  if (
  view === "admin" ||
  view === "teacher" ||
  view === "supervisor"
) return;
  async function loadInitial() {
    try {
      setLoading(true);
      setCheckInError(null);

      const privacyResponse = await fetch("/memberships/privacy-consent", { headers: authHeaders(), cache: "no-store" });
      if (!privacyResponse.ok) throw new Error("Could not check privacy settings");
      const privacy = await privacyResponse.json();
      const [p, m, h, j] = await Promise.all([
      getProfile(),
      getRecentMemories(100),
      privacy.applies && !privacy.granted ? Promise.resolve(null) : getHomeSummary(),
      getJournalEntries(),
    ]);

      setProfile(p);
      if (p.onboarding_version < 1 && !onboardingProject) {
        setViewPersist("firstSteps");
      }
      setMemories(m);
      setHomeSummary(h);
      setJournalEntries(j);
    } catch (err: any) {
      console.error(err);
      setError("Could not load your home data.");
    } finally {
      setLoading(false);
    }
  }

  loadInitial();
}, [token, authReady, authTick, view, onboardingProject]);

useEffect(() => {
  let alive = true;

  async function boot() {
    if (!token) {
      if (alive) setAuthReady(true);
      return;
    }

    try {
      const user = await validateToken();
      if (!alive) return;

      if (!user) {
        logout();
        setToken(null);
        return;
      }

      if (user.role === "admin") {
        setView("admin");
      } else if (user.role === "supervisor") {
        setView("supervisor");
      } else if (user.role === "teacher") {
        setView("teacher");
      } else {
        setView("home");
      }
    } finally {
      if (alive) setAuthReady(true);
    }
  }

  setAuthReady(false);
  boot();

  return () => {
    alive = false;
  };
}, [token]);
// ...your hooks above...

useEffect(() => {
  if (!token || !authReady) return;
  if (view !== "reflections") return;
  if (weeklyReflection) return;
  if (loadingReflection) return;
  if (errorReflection) return;

  void loadWeeklyReflection();
}, [
  token,
  authReady,
  view,
  weeklyReflection,
  loadingReflection,
  errorReflection,
  loadWeeklyReflection,
]);

// ✅ Render gates AFTER hooks
if (!authReady) {
  return <div>Loading…</div>;
}

if (window.location.pathname === "/confirmed") {
  return <ConfirmedPage />;
}

if (window.location.pathname === "/membership-confirmed") {
  return (
    <MembershipConfirmedPage
      onLogin={() => {
        window.history.replaceState({}, "", "/");
        setHasEntered(true);
      }}
    />
  );
}

if (window.location.pathname === "/workshop") {
  return <WorkshopPage />;
}

if (window.location.pathname === "/secure-workshop") {
  return <SecureWorkshopPage />;
}

if (window.location.pathname === "/thankyou") {
  return <WorkshopPage />;
}

const marketingPage = ({
  "/business-owners": "business",
  "/career-change": "career",
  "/finish-your-project": "projects",
  "/start-a-side-business": "side",
} as const)[window.location.pathname.replace(/\/$/, "") as "/business-owners"];
if (marketingPage) {
  return <CbcMarketingPage pageKey={marketingPage} onLogin={() => { window.location.href = "https://app.calmbutcapable.com/?login=1"; }} />;
}

if (window.location.pathname === "/organisations") {
  return <OrganisationsEntryScreen onLogin={() => setHasEntered(true)} />;
}

if (window.location.pathname === "/book-thank-you") {
  return <BookThankYouPage />;
}

if (window.location.pathname === "/individuals") {
  return <BookOfferPage onLogin={() => setHasEntered(true)} />;
}

if (window.location.pathname === "/sanctuary") {
  return (
    <SanctuaryEntryScreen
      onEnter={() => {
        window.history.replaceState({}, "", "/");
        setHasEntered(true);
      }}
    />
  );
}

if (!token) {
  if (view === "thankyou") {
    return (
     <ThankYouPage
        onContinue={() => {
          setHasEntered(true);
          setViewPersist("home");
        }}
      />
    );
  }

  if (!hasEntered) {
    return (
      <PublicWelcomeScreen onLogin={() => setHasEntered(true)} />
    );
  }

  return (
    <Login
      onSuccess={(accessToken) => {
        localStorage.setItem("token", accessToken);
        setToken(accessToken);
        setAuthTick((x) => x + 1);
      }}
    />
  );
}
 
if (view === "admin") {
  return <AdminDashboard onLogout={handleLogout} />;
}

if (view === "teacher" || view === "supervisor") {
  return <TeacherDashboard onLogout={handleLogout} />;
}

   async function handleCreateMemory() {
    if (!checkInText.trim()) return;
    try {
      setLoading(true);
      setError(null);
      const created = await createMemory(checkInText.trim(), "checkin", energyLevel);

setMemories((prev) => [created, ...prev]);

setHomeSummary((prev: any) =>
  prev
    ? {
        ...prev,
        recent_checkin: {
          id: created.id,
          raw_text: created.raw_text,
          energy_level: created.energy_level,
          created_at: created.created_at,
        },
      }
    : prev
);

setCheckInText("");
setCurrentEnergy(created.energy_level ?? null);
setEnergyLevel(null);

const today = new Date().toISOString().slice(0, 10);
localStorage.setItem(LAST_CHECKIN_DATE_KEY, today);
setStateCheckInComplete(true);

setJustCheckedIn(true);
setViewPersist("home");

    } catch (err: any) {
      console.error(err);
      setCheckInError("Could not save your check-in.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteMemory(id: number) {
  const ok = window.confirm("Delete this entry?");
  if (!ok) return;

  try {
    setLoading(true);
    setError(null);
    await deleteMemory(id);
    setMemories((prev) => prev.filter((m) => m.id !== id));
  } catch (err) {
    console.error(err);
    setError("Could not delete that entry.");
  } finally {
    setLoading(false);
  }
}

  async function handleCreateJournal() {
  if (!journalText.trim()) return;

  try {
    setJournalSaving(true);
    setJournalError(null);

    const created = await createJournalEntry(journalText.trim());
    setJournalEntries((prev) => [created, ...prev]);
    setJournalText("");
    setEditingJournalId(null);
  } catch (err: any) {
    console.error(err);
    setJournalError("Could not save your journal entry.");
  } finally {
    setJournalSaving(false);
  }
}

async function handleUpdateJournal() {
  if (!editingJournalId || !journalText.trim()) return;

  try {
    setJournalSaving(true);
    setJournalError(null);

    const updated = await updateJournalEntry(editingJournalId, journalText.trim());

    setJournalEntries((prev) =>
      prev.map((entry) => (entry.id === updated.id ? updated : entry))
    );

    setEditingJournalId(null);
    setJournalText("");
  } catch (err) {
    console.error(err);
    setJournalError("Could not update your journal entry.");
  } finally {
    setJournalSaving(false);
  }
}

async function handleCaptureSessionNote(note: string) {
  const trimmed = note.trim();
  if (!trimmed) return;

  try {
    setJournalSaving(true);
    setJournalError(null);

    const created = await createJournalEntry(trimmed);
    setJournalEntries((prev) => [created, ...prev]);
  } catch (err) {
    console.error(err);
    setJournalError("Could not save your session note.");
  } finally {
    setJournalSaving(false);
  }
}
async function handleDeleteJournal(id: string) {
  const ok = window.confirm("Delete this journal entry?");
  if (!ok) return;

  try {
    setJournalSaving(true);
    setJournalError(null);
    await deleteJournalEntry(id);
    setJournalEntries((prev) => prev.filter((entry) => entry.id !== id));

    if (editingJournalId === id) {
      setEditingJournalId(null);
      setJournalText("");
    }
  } catch (err) {
    console.error(err);
    setJournalError("Could not delete that journal entry.");
  } finally {
    setJournalSaving(false);
  }
}

// 3) Error (full-screen, but doesn’t pretend it’s onboarding)
if (error) {
  return (
    <div style={{ padding: 20 }}>
      <PrivacyConsentPanel />
      <h2 style={{ marginTop: 0 }}>Could not load</h2>
      <p style={{ opacity: 0.8 }}>{error}</p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 14px",
            borderRadius: 12,
            border: "1px solid #ccc",
            background: "white",
            cursor: "pointer",
          }}
        >
          Retry
        </button>

        <button
          onClick={() => setViewPersist("entrance")}
          style={{
            padding: "10px 14px",
            borderRadius: 12,
            border: "1px solid #ccc",
            background: "white",
            cursor: "pointer",
            opacity: 0.9,
          }}
        >
          Open “First 10 minutes”
        </button>
      </div>
    </div>
  );
}

// 4) Safe memories (prevents crashes)

const latestJournal = journalEntries[0];

const reflectionSuggestions = weeklyReflection?.suggestions ?? [];

const activeThreads = homeSummary?.active_threads ?? [];
const hasActiveThreads = activeThreads.length > 0;
const threadWithNextStep = activeThreads.find((thread: any) => thread.next_step?.title);
const hasNextStep = Boolean(threadWithNextStep);

const homeRecommendation = getHomeRecommendation({
  wellbeingPromptsEnabled: profile?.wellbeing_prompts_enabled ?? false,
  hasCheckInToday: stateCheckInComplete,
  mood: null,
  energy: currentEnergy,
  hasActiveThread: hasActiveThreads,
  hasNextStep: hasNextStep,
  hasJournalEntries: journalEntries.length > 0,
});

let homeMessage = homeRecommendation.reason;

  return (

  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      background: "#050608",
      color: "#e5e5e5",
    }}
  >
    <PrivacyConsentPanel />
    {view !== "entrance" && (
    <header
      style={{
        padding: "1rem 1.5rem",
        borderBottom: "1px solid #e5e7eb",
        background: "#ffffff",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
  <img
    src="/logo.png"
    alt="Calm But Capable"
    style={{ height: "60px", marginBottom: "2px", width: "fit-content", opacity: 0.85 }}
  />

  <div style={{ fontSize: "1.1rem", fontWeight: 600, color: "#111827" }}>
    {profile ? `Welcome, ${profile.display_name}` : "Welcome"}
  </div>

  <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
    Supported by Calm But Capable
  </div>
</div>

      <nav style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
  <NavButton label="Home" active={view === "home"} onClick={() => setViewPersist("home")} />
  <NavButton label="Check-In" active={view === "checkin"} onClick={() => setViewPersist("checkin")} />
  <NavButton label="Journal" active={view === "journals"} onClick={() => setViewPersist("journals")} />
  <NavButton label="Reflections" active={view === "reflections"} onClick={() => setViewPersist("reflections")} />
  <NavButton label="Toolkit" active={view === "toolkit"} onClick={() => setViewPersist("toolkit")} />
  <NavButton label="Getting Started" active={view === "firstSteps"} onClick={() => setViewPersist("firstSteps")} />
   <NavButton label="Workspace" active={view === "workspace"} onClick={openWorkspace}
  />
  <button
  type="button"
  onClick={handleLogout}
  style={{
    padding: "0.45rem 0.8rem",
    borderRadius: 999,
    border: "1px solid #9ca3af",
    color: "#374151",
    background: "transparent",
    cursor: "pointer",
  }}
>
  Log out
</button>
</nav>
    </header>
)}

    <main
      style={{
        flex: 1,
        padding: "1.5rem",
        maxWidth: 1100,
        width: "100%",
        margin: "0 auto",
      }}
    >

    {loading && (
  <p style={{ opacity: 0.8, fontSize: "1.1rem" }}>
    Loading…
  </p>
)}
        {view === "entrance" && (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      width: "100%",
    }}
  >
    <div style={{ width: "100%", maxWidth: 720 }}>
      <SanctuaryEntranceFlow
        onFinish={() => {
  setViewPersist("handoff");
}}
      />
    </div>
  </div>
)}


   {view === "handoff" && (
  <GentleHandoff
    onChoose={(choice) => {
      saveHandoffChoice(choice);

      if (choice === "one_small_step") return setViewPersist("checkin");
      if (choice === "clarity_check") return setViewPersist("reflections");
      return setViewPersist("home"); // coffee_time OR done_for_now
    }}
  />
)}
        {view === "thankyou" && (
  <ThankYouPage
    onContinue={() => setViewPersist("home")}
  />
)}

        {view === "home" && (
  <>

   {profile?.wellbeing_prompts_enabled && !stateCheckInComplete && !wellbeingPromptDismissed ? (
     <div className="wellbeing-invitation">
       <div>
         <strong>Would a quick wellbeing check-in help today?</strong>
         <p>This is optional. You can open it now, dismiss it, or turn reminders off from Check-In.</p>
       </div>
       <div className="wellbeing-invitation__actions">
         <button type="button" onClick={() => setViewPersist("checkin")}>Open Check-In</button>
         <button type="button" onClick={() => setWellbeingPromptDismissed(true)}>Not today</button>
       </div>
     </div>
   ) : null}

   <HomeView
  justCheckedIn={justCheckedIn}
  clearJustCheckedIn={() => setJustCheckedIn(false)}

  homeMessage={homeMessage}
  lastHandoff={lastHandoff}
  prettyChoice={prettyChoice}
  homeSummary={homeSummary}
  setWorkspaceThreadId={setWorkspaceThreadId}
  setViewPersist={setViewPersist}
  latestJournal={latestJournal}
  checkIns={checkIns}
  wellbeingPromptsEnabled={profile?.wellbeing_prompts_enabled ?? false}
  handleDeleteMemory={handleDeleteMemory}
  homeRecommendation={homeRecommendation}
  openWorkspace={openWorkspace}
/>
  </>
)}

{view === "checkin" && (
  <section>
    <h2 style={{ marginTop: 0 }}>Today’s Check-In</h2>
    <p style={{ opacity: 0.8, fontSize: "1.1rem" }}>
      Take a moment to note your energy levels. It helps you set a realistic pace for your work.
    </p>

    {profile ? (
      <div className="wellbeing-preference">
        <div>
          <strong>Home-page check-in reminders</strong>
          <p>The Check-In remains available whether reminders are on or off.</p>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              const updated = await updateProfilePreferences({
                wellbeing_prompts_enabled: !profile.wellbeing_prompts_enabled,
              });
              setProfile(updated);
              setWellbeingPromptDismissed(false);
            } catch (err) {
              setCheckInError(err instanceof Error ? err.message : "Could not save your preference");
            }
          }}
        >
          {profile.wellbeing_prompts_enabled ? "Turn reminders off" : "Turn reminders on"}
        </button>
      </div>
    ) : null}

   <div style={writingColumn}>
  <div style={{ marginTop: "1rem", marginBottom: "0.75rem" }}>
    <div style={{ opacity: 0.85, fontSize: "0.95rem", marginBottom: "0.5rem" }}>
      Energy today
    </div>

    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
      {[1, 2, 3, 4, 5].map((level) => (
        <button
          key={level}
          type="button"
          onClick={() => setEnergyLevel(level)}
          style={{
            padding: "0.5rem 0.9rem",
            borderRadius: 999,
            border: energyLevel === level ? "1px solid #63b3ed" : "1px solid #444",
            background: energyLevel === level ? "rgba(43, 108, 176, 0.25)" : "transparent",
            color: energyLevel === level ? "#fff" : "#ccc",
            cursor: "pointer",
            fontSize: "0.95rem",
          }}
        >
          {level}
        </button>
      ))}

      <button
        type="button"
        onClick={() => setEnergyLevel(null)}
        style={{
          padding: "0.5rem 0.9rem",
          borderRadius: 999,
          border: energyLevel === null ? "1px solid #63b3ed" : "1px solid #444",
          background: energyLevel === null ? "rgba(43, 108, 176, 0.25)" : "transparent",
          color: energyLevel === null ? "#fff" : "#ccc",
          cursor: "pointer",
          fontSize: "0.95rem",
        }}
      >
        Skip
      </button>
    </div>

    <div style={{ marginTop: "0.4rem", fontSize: "0.85rem", opacity: 0.65 }}>
      Optional. 1 = very low, 5 = very high.
    </div>
  </div>
  {checkInError && (
  <div
    style={{
      marginTop: "1rem",
      marginBottom: "0.75rem",
      padding: "0.75rem 1rem",
      borderRadius: 8,
      background: "#401919",
      border: "1px solid #a33",
      fontSize: "0.95rem",
    }}
  >
    {checkInError}
  </div>
)}
  <textarea
    className="sanctuary-textarea"
        value={checkInText}
        onChange={(e) => setCheckInText(e.target.value)}
        placeholder="Anything you want to note before you begin? This could be how you're feeling or what you want to work on."
        rows={6}
        style={{ ...inputStyles, resize: "vertical" }}
      />

      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem" }}>
        <button
          onClick={handleCreateMemory}
          style={{
            padding: "0.6rem 1.2rem",
            borderRadius: 999,
            border: "none",
            background: "#0991ED",
            color: "white",
            fontSize: "1.1rem",
            cursor: "pointer",
          }}
        >
          Save Check-In
        </button>

       <button
          onClick={() => setViewPersist("home")}
          style={{
            padding: "0.6rem 1.2rem",
            borderRadius: 999,
            border: "1px solid #374151",
            background: "transparent",
            color: "#9ca3af",
            fontSize: "1.1rem",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>

      <div style={{ marginTop: "2rem" }}>
        <h3>Past check-ins</h3>

        {checkIns.length === 0 ? (
          <p style={{ opacity: 0.7 }}>You do not have any saved check-ins yet.</p>
        ) : (
          checkIns.map((checkIn) => (
            <article
              key={checkIn.id}
              style={{
                padding: "0.9rem 1rem",
                borderRadius: 8,
                border: "1px solid #222",
                background: "#111",
                marginBottom: "0.75rem",
              }}
            >
              <div style={{ opacity: 0.65, fontSize: "0.9rem" }}>
                {new Date(checkIn.created_at).toLocaleString()}
                {checkIn.energy_level !== null && checkIn.energy_level !== undefined
                  ? ` · Energy ${checkIn.energy_level}/5`
                  : ""}
              </div>

              <div style={{ marginTop: "0.5rem", whiteSpace: "pre-wrap" }}>
                {checkIn.raw_text}
              </div>

              <div style={{ marginTop: "0.75rem", textAlign: "right" }}>
                <button
                  type="button"
                  onClick={() => handleDeleteMemory(checkIn.id)}
                  style={{
                    padding: "0.3rem 0.7rem",
                    borderRadius: 999,
                    border: "1px solid #555",
                    background: "transparent",
                    color: "#bbb",
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  </section>
)}

       {view === "toolkit" && <ToolkitView />}

       {view === "journals" && (
  <>

    <JournalsView
      journalText={journalText}
      setJournalText={setJournalText}
      handleCreateJournal={handleCreateJournal}
      handleUpdateJournal={handleUpdateJournal}
      journalSaving={journalSaving}
      journalError={journalError}
      journalEntries={journalEntries}
      handleDeleteJournal={handleDeleteJournal}
      handleStartEditJournal={handleStartEditJournal}
      handleCancelEditJournal={handleCancelEditJournal}
      editingJournalId={editingJournalId}
      writingColumn={writingColumn}
      inputStyles={inputStyles}
    />
  </>
)}

       {view === "reflections" && (
  <>

    <ReflectionsView
      loadingReflection={loadingReflection}
      weeklyReflection={weeklyReflection}
      reflectionSuggestions={reflectionSuggestions}
    />
  </>
)}

{view === "firstSteps" && (
  <FirstLoginOnboarding
    displayName={profile?.display_name ?? "there"}
    threadId={workspaceThreadId}
    onPrepareWorkspace={ensureWorkspace}
    onOpenProject={(plan, wellbeingPromptsEnabled) => {
      setOnboardingWellbeingChoice(wellbeingPromptsEnabled);
      setOnboardingProject(true);
      setActiveProject(plan);
      setViewPersist("projectWorkspace");
    }}
  />
)}

 {view === "workspace" && (
  <section>

    <h2 style={{ marginTop: 0 }}>Thread Workspace</h2>
    <p style={{ opacity: 0.8, fontSize: "1.1rem" }}>
      Turn a plan into clear, actionable steps and focus on the next open step.
    </p>
{workspaceThreadId !== null ? (
  <>
    
    <ThreadWorkspace
      threadId={workspaceThreadId}
      onOpenProject={(plan) => {
        setOnboardingProject(false);
        setActiveProject(plan);
        setViewPersist("projectWorkspace");
      }}
    />
  </>
) : (
  <div
    style={{
      marginTop: "1.5rem",
      padding: "1rem",
      borderRadius: 10,
      border: "1px solid #222",
      background: "#111",
      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
    }}
  >
    <p style={{ marginTop: 0 }}>
      You don’t have a workspace thread yet.
    </p>
    <button
  type="button"
  onClick={openWorkspace}
>
  Start a workspace
</button>
  </div>
)}

  </section>
)}

  {view === "projectWorkspace" && activeProject && (
  <ProjectWorkspace
    planId={activeProject.id}
    planTitle={activeProject.title}
    planDescription={activeProject.description}
    onBack={() => setViewPersist("workspace")}
    onCaptureSessionNote={handleCaptureSessionNote}
    introduction={onboardingProject}
    onFinishIntroduction={async () => {
      const updated = await updateProfilePreferences({
        onboarding_version: 1,
        wellbeing_prompts_enabled: onboardingWellbeingChoice,
      });
      setProfile(updated);
      setOnboardingProject(false);
    }}
  />
)}

{view === "projectWorkspace" && !activeProject && (
  <section>
    <h2 style={{ marginTop: 0 }}>Workspace</h2>
    <p style={{ opacity: 0.8 }}>
      Choose a project to continue.
    </p>

    {workspaceThreadId !== null ? (
      <ThreadWorkspace
        threadId={workspaceThreadId}
        onOpenProject={(plan) => {
          setOnboardingProject(false);
          setActiveProject(plan);
          setViewPersist("projectWorkspace");
        }}
      />
    ) : (
      <button
  type="button"
  onClick={openWorkspace}
  style={{
    padding: "0.6rem 1.2rem",
    borderRadius: 999,
    border: "none",
    background: "#0991ED",
    color: "#ffffff",
    fontSize: "1rem",
    cursor: "pointer",
  }}
>
  Start a workspace
</button>
    )}
  </section>
)}
      </main>
    </div>
  );
}

type NavButtonProps = {
  label: string;
  active: boolean;
  onClick: () => void;
};

function NavButton({ label, active, onClick }: NavButtonProps) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "#e6f2fb";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = "scale(0.97)";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
      style={{
        padding: "0.45rem 0.9rem",
        borderRadius: 999,
        border: active ? "none" : "1px solid transparent",
        background: active ? "#0991ED" : "transparent",
        color: active ? "#ffffff" : "#374151",
        fontWeight: 500,
        cursor: "pointer",
        transition: "all 0.15s ease",
      }}
    >
      {label}
    </button>
  );
}

export default App;
